import React, { useState, useMemo, useEffect } from "react";
import { Loader2, Check, X, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, setUser as setStoredUser } from "../lib/api";
import { useAuth } from "../lib/auth";
import { COUNTRIES, countryOf, CHARGEABLE_CURRENCIES } from "../lib/countries";

/** Small dismissible card that nudges legacy users to fill in their billing
 * address + phone country code so their next PayGlocal / Razorpay checkout
 * pre-fills for them. Auto-hides once profile is complete OR dismissed.
 *
 * "Complete" = has first_name, phone, and billing.{country, address_line1, city, state, postal_code}.
 */
export default function ProfileCompletionCard() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setDismissed(localStorage.getItem("optivize_profile_prompt_dismissed_v1") === "1");
    }
  }, []);

  const missing = useMemo(() => {
    if (!user) return [];
    const b = user.billing || {};
    const gaps = [];
    if (!user.first_name) gaps.push("Name");
    if (!user.phone) gaps.push("Phone");
    if (!b.country) gaps.push("Country");
    if (!b.address_line1) gaps.push("Address");
    if (!b.city) gaps.push("City");
    if (!b.state) gaps.push("State");
    if (!b.postal_code) gaps.push("Postal");
    return gaps;
  }, [user]);

  if (!user || missing.length === 0 || dismissed) return null;

  const dismiss = () => {
    if (typeof window !== "undefined") localStorage.setItem("optivize_profile_prompt_dismissed_v1", "1");
    setDismissed(true);
  };

  return (
    <>
      <div
        className="card p-5 border border-cyan-400/30 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 flex flex-col sm:flex-row sm:items-center gap-4"
        data-testid="profile-completion-card"
      >
        <div className="shrink-0 h-11 w-11 rounded-full bg-cyan-500/15 flex items-center justify-center">
          <MapPin className="text-cyan-300" size={20}/>
        </div>
        <div className="flex-1">
          <div className="text-white font-semibold" style={{fontFamily:'Outfit'}}>
            Complete your profile
          </div>
          <p className="text-slate-300 text-xs mt-1">
            Your next checkout will pre-fill automatically — save 2 minutes on every payment.
            Missing: <span className="text-cyan-300 font-medium">{missing.join(" · ")}</span>
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={()=>setOpen(true)}
            className="btn-primary inline-flex items-center gap-1.5 text-sm"
            data-testid="complete-profile-open-btn"
          >
            Complete now <ArrowRight size={14}/>
          </button>
          <button
            onClick={dismiss}
            className="btn-ghost !py-2 !px-3 text-xs"
            title="Not now"
            data-testid="complete-profile-dismiss-btn"
          >
            Not now
          </button>
        </div>
      </div>
      {open && <BillingProfileModal onClose={()=>setOpen(false)} onSaved={(u)=>{ setStoredUser(u); setUser(u); setOpen(false); }} user={user}/>}
    </>
  );
}

function BillingProfileModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: user.first_name || "",
    last_name:  user.last_name  || "",
    phone:      user.phone      || "",
    billing_country: (user.billing?.country) || "IN",
    preferred_currency: user.preferred_currency || countryOf(user.billing?.country || "IN").currency,
    address_line1: user.billing?.address_line1 || "",
    address_line2: user.billing?.address_line2 || "",
    city:          user.billing?.city          || "",
    state:         user.billing?.state         || "",
    postal_code:   user.billing?.postal_code   || "",
    company:       user.company                || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const country = useMemo(() => countryOf(form.billing_country), [form.billing_country]);

  // Auto-sync phone dial prefix + currency when country changes
  useEffect(() => {
    setForm((f) => {
      const stripped = (f.phone || "").replace(/^\+\d{1,4}\s*/, "");
      const nextPhone = !f.phone || /^\+\d/.test(f.phone) ? `${country.dial} ${stripped}`.trim() : f.phone;
      const nextCurr = CHARGEABLE_CURRENCIES.includes(country.currency) ? country.currency : "USD";
      return { ...f, phone: nextPhone, preferred_currency: f.preferred_currency || nextCurr };
    });
  }, [form.billing_country, country.dial, country.currency]);

  const save = async () => {
    // required fields
    const required = ["first_name","last_name","phone","address_line1","city","state","postal_code","billing_country"];
    const miss = required.filter(k => !(form[k] || "").trim());
    if (miss.length) { toast.error("Please fill all required fields"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", form);
      toast.success("Profile updated — checkout will pre-fill from now on");
      onSaved(data.user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="profile-modal">
      <div className="card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="profile-modal-close">
          <X size={18}/>
        </button>
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Complete your profile</h2>
        <p className="text-slate-400 text-sm mt-1">Used to pre-fill your Razorpay + PayGlocal checkout billing forms.</p>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <Fld label="First name *" v={form.first_name} on={set("first_name")} t="pf-first-name"/>
          <Fld label="Last name *"  v={form.last_name}  on={set("last_name")}  t="pf-last-name"/>

          <div>
            <label className="label">Billing country *</label>
            <select value={form.billing_country} onChange={set("billing_country")} className="input w-full" data-testid="pf-country">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name} ({c.code})</option>)}
            </select>
          </div>
          <div>
            <label className="label">Currency *</label>
            <select value={form.preferred_currency} onChange={set("preferred_currency")} className="input w-full" data-testid="pf-currency">
              {CHARGEABLE_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Phone *</label>
            <div className="flex gap-2">
              <span className="input flex items-center px-3 text-slate-300 shrink-0" style={{minWidth:'70px'}} data-testid="pf-dial">{country.dial}</span>
              <input
                value={(form.phone || "").replace(new RegExp(`^\\${country.dial}\\s*`), "")}
                onChange={(e)=>setForm(f=>({...f, phone: `${country.dial} ${e.target.value.replace(/^\+?\d{0,4}\s*/, '')}`.trim() }))}
                className="input w-full" data-testid="pf-phone" inputMode="tel"
              />
            </div>
          </div>

          <Fld label="Address line 1 *" v={form.address_line1} on={set("address_line1")} t="pf-addr1" span/>
          <Fld label="Address line 2"   v={form.address_line2} on={set("address_line2")} t="pf-addr2" span/>
          <Fld label="City *"           v={form.city}          on={set("city")}          t="pf-city"/>
          <Fld label="State / Region *" v={form.state}         on={set("state")}         t="pf-state"/>
          <Fld label="Postal / ZIP *"   v={form.postal_code}   on={set("postal_code")}   t="pf-postal"/>
          <Fld label="Company (optional)" v={form.company}     on={set("company")}       t="pf-company"/>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={save} disabled={saving} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="pf-save-btn">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Check size={16}/>}
            Save profile
          </button>
          <button onClick={onClose} className="btn-ghost" data-testid="pf-cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, v, on, t, span = false }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="label">{label}</label>
      <input value={v} onChange={on} className="input w-full" data-testid={t}/>
    </div>
  );
}
